import type { Permission } from '@kortex/shared';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { useAuth } from './lib/auth';
import { AuditPage, SettingsPage, UsersPage } from './pages/Admin';
import { DashboardPage } from './pages/Dashboard';
import { FrontDeskPage } from './pages/FrontDesk';
import { GstPage, ReportsPage } from './pages/Insights';
import { InventoryPage } from './pages/Inventory';
import { InvoicesPage } from './pages/Invoices';
import { KitchenPage } from './pages/Kitchen';
import { MenuPage } from './pages/Menu';
import { PeoplePage } from './pages/People';
import { PosPage } from './pages/Pos';
import { ProcurementPage } from './pages/Procurement';
import { HousekeepingPage, ReservationsPage } from './pages/Reservations';
import { SignIn } from './pages/SignIn';

/** A route the signed-in role is not allowed to open. */
function NotAllowed() {
  return (
    <div className="card">
      <div className="empty">
        <span className="big">⚿</span>
        Your role does not have access to this screen.
      </div>
    </div>
  );
}

function Guard({ permission, children }: { permission: Permission; children: React.ReactNode }) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <NotAllowed />;
}

/** The first screen a role can actually open, so nobody lands on a wall. */
function LandingRedirect() {
  const { can } = useAuth();
  if (can('dashboard:view')) return <DashboardPage />;
  if (can('kds:view')) return <Navigate to="/kitchen" replace />;
  if (can('housekeeping:view')) return <Navigate to="/housekeeping" replace />;
  if (can('rooms:view')) return <Navigate to="/front-desk" replace />;
  return <NotAllowed />;
}

export function App() {
  const { status } = useAuth();

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Loading label="Restoring your session…" />
      </div>
    );
  }

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/sign-in" element={<Navigate to="/" replace />} />
      <Route element={<Layout />}>
        <Route index element={<LandingRedirect />} />
        <Route path="pos" element={<Guard permission="pos:operate"><PosPage /></Guard>} />
        <Route path="kitchen" element={<Guard permission="kds:view"><KitchenPage /></Guard>} />
        <Route path="front-desk" element={<Guard permission="rooms:view"><FrontDeskPage /></Guard>} />
        <Route path="reservations" element={<Guard permission="reservation:view"><ReservationsPage /></Guard>} />
        <Route path="housekeeping" element={<Guard permission="housekeeping:view"><HousekeepingPage /></Guard>} />
        <Route path="invoices" element={<Guard permission="invoice:view"><InvoicesPage /></Guard>} />
        <Route path="menu" element={<Guard permission="menu:view"><MenuPage /></Guard>} />
        <Route path="inventory" element={<Guard permission="inventory:view"><InventoryPage /></Guard>} />
        <Route path="procurement" element={<Guard permission="procurement:view"><ProcurementPage /></Guard>} />
        <Route path="people" element={<Guard permission="hr:view"><PeoplePage /></Guard>} />
        <Route path="reports" element={<Guard permission="reports:view"><ReportsPage /></Guard>} />
        <Route path="gst" element={<Guard permission="gst:export"><GstPage /></Guard>} />
        <Route path="users" element={<Guard permission="users:view"><UsersPage /></Guard>} />
        <Route path="settings" element={<Guard permission="settings:view"><SettingsPage /></Guard>} />
        <Route path="audit" element={<Guard permission="audit:view"><AuditPage /></Guard>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
